// The three company-scoped internal reads behind the Support / Tours / Ask tabs on
// the internal company detail page (VRG-516, against the endpoints VRG-515 built).
//
// One file rather than three, because they are one feature and share every option:
// same staleTime, same retry policy, same "a 403 is an answer" contract. Splitting
// them would be three files that must be kept identical.
import { useQuery } from '@tanstack/react-query'

import { askStatsApi, supportApi, tourAnalyticsApi } from 'services'

/**
 * `retry: false` on all three, and it is load-bearing rather than a default copied
 * across.
 *
 * Each of these can legitimately answer 403: Support needs `internal_support_manage`,
 * Tours and Ask need `internal_analytics_view` (Ask since 2026-10-08, after VRG-685), and
 * a staffer can hold one without the other. A 403 is a SETTLED answer, not a transient failure —
 * retrying it three times delays the panel by seconds to arrive at the same result,
 * and (worse) makes a permission problem look like a network problem while it does.
 * The panels branch on `error.status === 403` to say "you cannot look at this",
 * which must never render as "there is nothing here".
 *
 * `staleTime` 5 minutes matches the cross-company pages these mirror. A company
 * detail page is read while someone is on a call about that company, so a tab switch
 * re-rendering from cache is right; none of this is live telemetry. The tour endpoint
 * additionally caches server-side, so `computedAt` can be older than the fetch.
 */
const INSIGHT_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  retry: false,
} as const

/**
 * `enabled: !!companyId` on each: the company id arrives from a route param and is
 * `undefined` on the first render of the page. Firing without it would build a URL
 * with a literal `undefined` segment, which the API answers 400 — a spurious error
 * state on a page that is merely still loading.
 */
export const useCompanySupportRequests = (companyId?: string) =>
  useQuery({
    queryKey: ['internal', 'company', companyId, 'support-requests'],
    queryFn: () => supportApi.companyRequests(companyId as string),
    enabled: !!companyId,
    ...INSIGHT_QUERY_OPTIONS,
  })

export const useCompanyTourAnalytics = (companyId?: string) =>
  useQuery({
    queryKey: ['internal', 'company', companyId, 'tour-analytics'],
    queryFn: () => tourAnalyticsApi.companyAnalytics(companyId as string),
    enabled: !!companyId,
    ...INSIGHT_QUERY_OPTIONS,
  })

export const useCompanyAskStats = (companyId?: string) =>
  useQuery({
    queryKey: ['internal', 'company', companyId, 'ask-stats'],
    queryFn: () => askStatsApi.companyStats(companyId as string),
    enabled: !!companyId,
    ...INSIGHT_QUERY_OPTIONS,
  })
